import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Brain,
  Layers,
  ScanLine,
  Target,
  MemoryStick,
  Cpu,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Database,
  BookOpen,
  Scale,
  Rocket,
  Zap,
  Copy,
  Check,
  Sparkles,
} from 'lucide-react';
import { MONAIBundle, MONAITaskType, MONAIModality } from '@/mock/gpu-instance';

interface MONAIBundleCardProps {
  bundle: MONAIBundle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectBundle?: (bundle: MONAIBundle) => void;
}

const getTaskIcon = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return <Layers className="h-5 w-5 text-purple-500" />;
    case 'classification':
      return <ScanLine className="h-5 w-5 text-blue-500" />;
    case 'detection':
      return <Target className="h-5 w-5 text-green-500" />;
    case 'registration':
      return <Brain className="h-5 w-5 text-orange-500" />;
  }
};

const getTaskBadge = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <Layers className="h-3 w-3 mr-1" /> Segmentation
        </Badge>
      );
    case 'classification':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <ScanLine className="h-3 w-3 mr-1" /> Classification
        </Badge>
      );
    case 'detection':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Target className="h-3 w-3 mr-1" /> Detection
        </Badge>
      );
    case 'registration':
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          <Brain className="h-3 w-3 mr-1" /> Registration
        </Badge>
      );
  }
};

const getModalityColor = (modality: MONAIModality) => {
  const colors: Record<MONAIModality, string> = {
    CT: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    MRI: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    'X-Ray': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
    Ultrasound: 'bg-green-500/10 text-green-600 border-green-500/30',
    Pathology: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
    Fundus: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    Dermoscopy: 'bg-red-500/10 text-red-600 border-red-500/30',
    Endoscopy: 'bg-teal-500/10 text-teal-600 border-teal-500/30',
  };
  return colors[modality];
};

const MONAIBundleCard = ({
  bundle,
  open,
  onOpenChange,
  onSelectBundle,
}: MONAIBundleCardProps) => {
  const [copiedPath, setCopiedPath] = useState(false);

  if (!bundle) return null;

  const mainMetric = bundle.diceScore || bundle.accuracy || bundle.auc || 0;
  const metricName = bundle.diceScore ? 'Dice Score' : bundle.accuracy ? 'Accuracy' : 'AUC';

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(true);
      setTimeout(() => setCopiedPath(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSelect = () => {
    onSelectBundle?.(bundle);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/10 to-pink-500/10">
                {getTaskIcon(bundle.task)}
              </div>
              <div>
                <DialogTitle className="text-xl">{bundle.displayName}</DialogTitle>
                <DialogDescription className="mt-1">
                  {bundle.description}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Task & Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {getTaskBadge(bundle.task)}
          <Badge className={getModalityColor(bundle.modality)}>
            {bundle.modality}
          </Badge>
          <Badge variant="outline" className="font-mono text-xs">
            v{bundle.version}
          </Badge>
        </div>

        <Separator className="my-4" />

        {/* Performance Metrics */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Performance Metrics
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  {metricName}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {(mainMetric * 100).toFixed(1)}%
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <MemoryStick className="h-3 w-3" />
                  VRAM Required
                </div>
                <div className="text-2xl font-bold">
                  {bundle.vramRequired} GB
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <Cpu className="h-3 w-3" />
                  Architecture
                </div>
                <div className="text-xl font-bold">
                  {bundle.architecture}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  Input Size
                </div>
                <div className="text-lg font-bold font-mono">
                  {bundle.inputSize}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Pretrained Information */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-500" />
            Pretrained Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Pretrained Dataset</div>
                <div className="font-medium">{bundle.pretrainedDataset}</div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground mb-1">Output Classes</div>
                <div className="font-medium text-lg">
                  {bundle.outputClasses.toLocaleString()}
                  <span className="text-xs text-muted-foreground ml-1">classes</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {bundle.task === 'segmentation' && 'Anatomical structures for segmentation'}
                  {bundle.task === 'classification' && 'Categories for classification'}
                  {bundle.task === 'detection' && 'Object categories for detection'}
                  {bundle.task === 'registration' && 'Alignment landmarks'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Output Class Names */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Prediction Classes ({bundle.outputClasses})
          </h3>
          <div className="flex flex-wrap gap-2">
            {bundle.outputClassNames.slice(0, 15).map((className, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {className}
              </Badge>
            ))}
            {bundle.outputClassNames.length > 15 && (
              <Badge variant="outline" className="text-xs">
                +{bundle.outputClassNames.length - 15} more
              </Badge>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Use Cases */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Rocket className="h-4 w-4 text-purple-500" />
            Recommended Use Cases
          </h3>
          <div className="flex flex-wrap gap-2">
            {bundle.useCases.map((useCase, idx) => (
              <Badge key={idx} variant="secondary" className="text-sm">
                {useCase}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Strengths & Limitations */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Strengths
            </h3>
            <ul className="space-y-2">
              {bundle.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              Limitations
            </h3>
            <ul className="space-y-2">
              {bundle.limitations.map((limitation, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span>{limitation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Supported Augmentations */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Supported MONAI Augmentations</h3>
          <div className="flex flex-wrap gap-2">
            {bundle.supportedAugmentations.map((aug) => (
              <Badge key={aug} variant="outline" className="text-xs font-mono">
                {aug}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Technical Details */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-500" />
            Technical Details
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground">NGC Path</span>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono">{bundle.ngcPath}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => handleCopy(bundle.ngcPath)}
                >
                  {copiedPath ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground">Architecture</span>
              <code className="text-sm font-mono">{bundle.architecture}</code>
            </div>
            <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Scale className="h-3 w-3" />
                License
              </span>
              <Badge variant="outline" className="text-xs">
                {bundle.license}
              </Badge>
            </div>
            {bundle.paperReference && (
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <span className="text-sm text-muted-foreground">Paper</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => window.open(bundle.paperReference, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View Paper
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() =>
              window.open(
                `https://catalog.ngc.nvidia.com/orgs/nvidia/teams/monailabel/models/${bundle.name}`,
                '_blank'
              )
            }
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View on NGC
          </Button>
          {onSelectBundle && (
            <Button onClick={handleSelect} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Bundle
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MONAIBundleCard;
